import { IJsonQuery } from '@cian/api-models/common/json_query';
import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Provider, Filters, IOnChangeParameters, TModifier } from '../../../packages/Filters';
import { IApplicationState, TThunkDispatch } from '../../types/redux';
import { IRegionSpecialPromo } from '../../types/specialPromos';
import { getAvailableSpecialPromo } from '../../utils/specialPromos';
import { changeJsonQuery, fetchTags } from '../../actions/filters';
import { fetchClusters } from '../../actions/results';
import { trackClearClick, trackAdvancedFiltersClick, trackApplyClick } from './tracking';
import { SaveSearchButtonContainer as SaveSearchButton } from './SaveSearchButton';

const GEO_CHANGE_ACTIONS: TModifier['action'][] = ['clearGeo', 'selectGeo', 'setBSCenter', 'setRegion', 'setVillage'];

export const FiltersContainer: React.FC = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const jsonQuery = useSelector<IApplicationState, IJsonQuery>(state => state.filters.jsonQuery);
  const specialPromos = useSelector<IApplicationState, IRegionSpecialPromo[]>(state => state.specialPromos);
  const queryString = useSelector<IApplicationState, string>(state => state.queryString);
  const [isAwaitingTracking, setIsAwaitingTracking] = React.useState<boolean>(false);
  const isAwaitingGeoUpdate = React.useRef<boolean>(false);
  const specialPromo: IRegionSpecialPromo | undefined = getAvailableSpecialPromo(specialPromos, jsonQuery);

  const handleChange = React.useCallback(
    (parameters: IOnChangeParameters) => {
      dispatch(changeJsonQuery({ ...parameters, prevJsonQuery: jsonQuery }));

      if (parameters.appliedModifiers.some(modifier => GEO_CHANGE_ACTIONS.includes(modifier.action))) {
        isAwaitingGeoUpdate.current = true;
        dispatch(fetchTags());
      }
    },
    [dispatch, jsonQuery],
  );

  const handleApply = React.useCallback(() => {
    dispatch(fetchClusters({ updateBounds: isAwaitingGeoUpdate.current }));
    isAwaitingGeoUpdate.current = false;
  }, [dispatch, isAwaitingGeoUpdate]);

  const handleApplyClick = React.useCallback(() => {
    setIsAwaitingTracking(true);
  }, []);

  React.useEffect(() => {
    if (isAwaitingTracking) {
      trackApplyClick(queryString);

      setIsAwaitingTracking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  return (
    <Provider
      jsonQuery={jsonQuery}
      specialPromo={specialPromo}
      onChange={handleChange}
      onApply={handleApply}
      onClear={trackClearClick}
      onApplyClick={handleApplyClick}
      onAdvancedFiltersOpen={trackAdvancedFiltersClick}
    >
      <Filters savedSearch={<SaveSearchButton />} />
    </Provider>
  );
};
